import { DestroyRef, Injectable, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Firestore, docData, collection, query, where, getDocs } from '@angular/fire/firestore';
import { Observable, from, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { Property } from '@shared/definitions/property';
import { AuthService } from '@core/services/auth/auth.service';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { WorkspaceService } from '@core/services/auth/workspace.service';
import { PropertyStore } from '@shared/services/property.store';
import { BaseCrudService } from '@core/services/data/base-crud.service';

/**
 * @domain Services
 * 
 * PropertyService migrated to use BaseCrudService.
 * 
 * This demonstrates the REPLACEMENT strategy:
 * ✅ All existing methods preserved with same signatures
 * ✅ Same public API - no breaking changes
 * ✅ Enhanced with optimistic updates, better error handling
 * ✅ ~70% less code while maintaining full functionality
 * 
 * Migration Strategy:
 * 1. Extend BaseCrudService instead of implementing from scratch
 * 2. Keep all existing public methods with same signatures
 * 3. Delegate CRUD operations to base service
 * 4. Keep property-specific business logic
 * 5. Replace original file when ready
 */
@Injectable({
  providedIn: 'root'
})
export class PropertyService extends BaseCrudService<Property> {
  // Maintain backward compatibility - same public API
  public properties = this.entitiesSubject; // Same BehaviorSubject reference

  private readonly propertyStore = inject(PropertyStore);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    firestore: Firestore,
    pathService: FirestorePathService,
    authService: AuthService,
    workspaceService: WorkspaceService
  ) {
    super(firestore, pathService, authService, {
      collectionName: 'properties',
      idField: 'propertyUID',
      enableOptimisticUpdates: true,
      enableSoftDeletes: false, // Disable soft deletes to trigger backend cascade deletion
      // BaseCrudService passes the raw Firestore doc as `unknown`; cast at the boundary.
      entityConstructor: (data: unknown) => new Property(data as Partial<Property>)
    }, undefined, workspaceService); // Pass WorkspaceService to parent

    // Phase 4 bridge: mirror the list into PropertyStore so consumers can
    // depend on the store's signal API without pulling this whole service.
    // Pattern: same as AuthService / WorkspaceService bridges.
    // Soft-deleted tombstones (deleted:true — e.g. a property hidden by the
    // billing grace-period cron) must never reach the UI store. enableSoftDeletes
    // is false here (user deletes are hard, to trigger the cascade), so we filter
    // explicitly rather than rely on the base getEntities() accessor.
    // `untracked` guards against NG0600: a BehaviorSubject replays its current
    // value SYNCHRONOUSLY on subscribe (and may emit again while Angular is
    // mid-computation). PropertyStore is read by many component `computed`s, so
    // PropertyService can be lazily constructed *during* a computed evaluation —
    // the replayed emission would then write `_list` inside that reactive
    // context and throw "Writing to signals is not allowed in a computed".
    // Running the store write untracked sets the active consumer to null,
    // making the signal write legal regardless of the calling context.
    this.entitiesSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(list => untracked(() => this.propertyStore.setList(
        list.filter(p => !(p as { deleted?: boolean }).deleted)
      )));
  }


  // ========================================
  // EXISTING PUBLIC API - MAINTAINED 100%
  // ========================================

  /**
   * Get properties by user (existing method signature)
   */
  getPropertiesByUser(): Observable<Property[]> {
    return this.getEntities();
  }

  /**
   * Get property by ID (existing method signature)
   */
  getPropertyById(propertyUID: string): Observable<Property | null> {
    return this.getEntityById(propertyUID);
  }

  /**
   * Get property by ID from Firestore (existing method signature)
   * Now uses the base service's built-in caching and error handling
   */
  getPropertyByIdFromFirestore(propertyUID: string): Observable<Property | null> {
    // Force reload from Firestore
    this.reloadEntities();
    return this.getEntityById(propertyUID);
  }

  /**
   * Fetch a SINGLE property document directly from Firestore by id.
   *
   * Unlike getPropertyById / getEntityById (which wait on the whole
   * workspace property list to sync into the store and time out otherwise),
   * this reads just the one document. The property detail page uses it so
   * it loads instantly even when the list hasn't synced yet — fixing the
   * slow spinner that would otherwise time out and bounce back to the list.
   *
   * Returns a live stream (kept fresh by Firestore); emits null if the
   * document doesn't exist or on error. `idField: 'propertyUID'` mirrors
   * the list mapping so the route id (the Firestore doc id) populates
   * `propertyUID` consistently.
   */
  getPropertyByIdDirect(propertyUID: string): Observable<Property | null> {
    if (!propertyUID) return of(null);

    try {
      const ref = this.getDocument(propertyUID);
      return docData(ref, { idField: 'propertyUID' }).pipe(
        map(data => {
          if (!data) return null;
          const property = new Property(data as Partial<Property>);
          this.parseDateFields(property as unknown as Record<string, unknown>);
          return property;
        }),
        catchError(error => {
          console.error(`PropertyService: Failed to fetch property ${propertyUID} directly:`, error);
          return of(null);
        })
      );
    } catch (error) {
      // getDocument throws synchronously when no workspace is loaded.
      console.error(`PropertyService: Error building direct fetch for ${propertyUID}:`, error);
      return of(null);
    }
  }

  /**
   * Add property (existing method signature)
   */
  addProperty(property: Omit<Property, 'propertyUID'>): Observable<string> {
    return this.createEntity(property);
  }

  /**
   * Delete property (existing method signature)
   * Now uses hard delete to trigger backend cascade deletion
   */
  deleteProperty(propertyUID: string): Observable<void> {
    return from(this.assertNoActiveContracts(propertyUID)).pipe(
      switchMap(() => this.deleteEntity(propertyUID)),
      catchError(error => {
        console.error(`PropertyService: Failed to delete property ${propertyUID}:`, error);
        throw error;
      })
    );
  }

  /**
   * Refuse to delete a property a contract still references. Deleted-out-
   * from-under-a-lease is exactly how #539 surfaced ("Aucune information sur
   * le bien disponible" on the contract page) — an unrelated failure
   * elsewhere triggered a cleanup that discarded a property whose contract
   * had already been saved. A direct, one-off query here (rather than
   * `ContractStore`/`getContractsByProperty`, which only reflect whatever
   * this session happened to have loaded already) is what makes the check
   * correct regardless of what else the client has hydrated.
   */
  private async assertNoActiveContracts(propertyUID: string): Promise<void> {
    if ((await this.countLinkedContracts(propertyUID)) > 0) {
      throw new Error(`property-has-active-contracts: ${propertyUID}`);
    }
  }

  /**
   * How many contracts (active or ended) still reference this property. The
   * delete flow asks this BEFORE opening its confirmation dialog, so a
   * property that cannot be deleted says so and says what lifts the block
   * (#693) instead of promising a cascade the guard then refuses.
   */
  async countLinkedContracts(propertyUID: string): Promise<number> {
    const contractsRef = collection(this.firestore, this.pathService.getWorkspaceCollection('contracts'));
    const snapshot = await getDocs(query(contractsRef, where('propertyUID', '==', propertyUID)));
    return snapshot.size;
  }

  /**
   * Update property (existing method signature)
   * Now includes optimistic updates automatically and notifies related services
   */
  updateProperty(propertyUID: string, propertyData: Partial<Property>): Observable<void> {
    return this.updateEntity(propertyUID, propertyData);
  }

  /**
   * Get property count (existing method signature)
   */
  getPropertyCount(): Promise<number> {
    return this.getEntityCount();
  }

  /**
   * Get total property value (existing method signature)
   */
  getTotalPropertyValue(): Promise<number> {
    return Promise.resolve(
      this.entitiesData.reduce((total, property) => total + (property.financials?.currentValue || 0), 0)
    );
  }

  /**
   * Soft delete property (existing method signature)
   */
  softDeleteProperty(propertyUID: string): Observable<void> {
    // Base service handles this automatically when enableSoftDeletes is true
    return this.deleteEntity(propertyUID);
  }

  /**
   * Restore property (existing method signature)
   */
  restoreProperty(propertyUID: string): Observable<void> {
    return this.restoreEntity(propertyUID);
  }

  /**
   * Get property name by UID (existing method signature)
   */
  getPropertyNameByUID$(propertyUID: string | null | undefined): Observable<string> {
    if (!propertyUID) return of('Unknown Property');
    
    return this.entities$.pipe(
      map(properties => {
        const property = properties.find(p => p.propertyUID === propertyUID);
        return property ? this.formatPropertyName(property) : 'Unknown Property';
      })
    );
  }

  /**
   * Update property optimistically (existing method signature)
   * Now built into base service automatically and notifies related services!
   */
  updatePropertyOptimistic(propertyUID: string, propertyData: Partial<Property>): Observable<Property> {
    return this.updateEntity(propertyUID, propertyData).pipe(
      switchMap(() => this.getEntityById(propertyUID)),
      map(property => {
        if (!property) {
          throw new Error('Property not found after update');
        }
        return property;
      })
    );
  }

  // ========================================
  // PROPERTY-SPECIFIC BUSINESS LOGIC
  // ========================================

  /**
   * Get formatted property name by UID using cached data (synchronous)
   */
  getPropertyNameByUID(propertyUID: string | null | undefined): string {
    if (!propertyUID) return 'Unknown Property';
    
    const property = this.entitiesData.find(p => p.propertyUID === propertyUID);
    return property ? this.formatPropertyName(property) : 'Unknown Property';
  }

  /**
   * Consistent property name formatting used throughout the application
   */
  private formatPropertyName(property: Property): string {
    // First try the property name
    if (property.name) {
      return property.name;
    }
    
    // Then try address street
    if (property.address?.street) {
      return property.address.street;
    }
    
    // Then try full address
    if (property.address) {
      const addressParts = [
        property.address.street,
        property.address.city,
        property.address.zip
      ].filter(Boolean);
      
      if (addressParts.length > 0) {
        return addressParts.join(', ');
      }
    }
    
    // Final fallback
    return 'Unknown Property';
  }

  // ========================================
  // ENHANCED FEATURES (NEW CAPABILITIES)
  // ========================================

  /**
   * NEW: Get properties with enhanced filtering
   */
  getPropertiesWithFilter(filter: (property: Property) => boolean): Observable<Property[]> {
    return this.getEntities().pipe(
      map(properties => properties.filter(filter))
    );
  }

  /**
   * NEW: Bulk update properties
   */
  updateMultipleProperties(updates: { id: string; data: Partial<Property> }[]): Observable<void[]> {
    const updateObservables = updates.map(update => 
      this.updateEntity(update.id, update.data)
    );
    return from(Promise.all(updateObservables.map(obs => obs.toPromise())));
  }

  /**
   * NEW: Get property statistics
   */
  getPropertyStatistics(): Observable<{
    totalCount: number;
    totalValue: number;
    averageValue: number;
    byType: Record<string, number>;
  }> {
    return this.getEntities().pipe(
      map(properties => {
        const totalCount = properties.length;
        const totalValue = properties.reduce((sum, p) => sum + (p.financials?.currentValue || 0), 0);
        const averageValue = totalCount > 0 ? totalValue / totalCount : 0;
        
        const byType = properties.reduce((acc, p) => {
          acc[p.type] = (acc[p.type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        return { totalCount, totalValue, averageValue, byType };
      })
    );
  }

}
